// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Pencil, Copy, Archive, Plus, HelpCircle, Loader2, Search, ChevronDown, ChevronRight, Link2 } from "lucide-react";
import { toast } from "sonner";
import { RichTextEditor, RichTextDisplay } from "@/design-system/forms/RichTextEditor";
import type { FaqCatalogItem } from "@/types/events";

const DEFAULT_CATEGORIES = ["general", "registration", "schedule", "payments", "accommodations"];
const UNIVERSAL = "__universal__";
const ALL = "__all__";

const EMPTY: Partial<FaqCatalogItem> = {
  question: "",
  answer: "",
  category: "general",
  sport_id: null,
  is_active: true,
  display_order: 0,
};

export default function FaqCatalogPage() {
  const { organization } = useOrganization();
  const orgId = organization?.id;

  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [items, setItems] = useState<FaqCatalogItem[]>([]);
  const [linkCounts, setLinkCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const [filterCategory, setFilterCategory] = useState<string>(ALL);
  const [filterSport, setFilterSport] = useState<string>(ALL);
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "archived">("active");
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState<Partial<FaqCatalogItem> | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [eventOptions, setEventOptions] = useState<{ id: string; name: string }[]>([]);
  const [chosenEvents, setChosenEvents] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("sports").select("id, name").order("name");
      setSports(data ?? []);
    })();
  }, []);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    let q = (supabase as any)
      .from("faq_catalog")
      .select("*")
      .or(`organization_id.eq.${orgId},organization_id.is.null`)
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: true });
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    setItems(data ?? []);

    // Link counts in one query
    const ids = (data ?? []).map((r: any) => r.id);
    if (ids.length) {
      const { data: links } = await (supabase as any)
        .from("event_faq_links")
        .select("faq_id")
        .in("faq_id", ids)
        .eq("is_active", true);
      const counts: Record<string, number> = {};
      (links ?? []).forEach((l: any) => {
        counts[l.faq_id] = (counts[l.faq_id] ?? 0) + 1;
      });
      setLinkCounts(counts);
    } else {
      setLinkCounts({});
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const categories = useMemo(() => {
    const set = new Set<string>(DEFAULT_CATEGORIES);
    items.forEach((i) => i.category && set.add(i.category));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (filterStatus === "active" && !it.is_active) return false;
      if (filterStatus === "archived" && it.is_active) return false;
      if (filterCategory !== ALL && it.category !== filterCategory) return false;
      if (filterSport !== ALL) {
        if (filterSport === UNIVERSAL && it.sport_id !== null) return false;
        if (filterSport !== UNIVERSAL && it.sport_id !== filterSport) return false;
      }
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !it.question.toLowerCase().includes(q) &&
          !it.answer.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [items, filterCategory, filterSport, filterStatus, search]);

  const sportName = (id: string | null) =>
    id === null ? "Universal" : sports.find((s) => s.id === id)?.name ?? "—";

  const openCreate = () => {
    setEditing({ ...EMPTY, organization_id: orgId, display_order: items.length });
  };

  const save = async () => {
    if (!editing?.question?.trim() || !editing?.answer?.trim()) {
      toast.error("Question and answer are required");
      return;
    }
    const payload: any = {
      organization_id: editing.organization_id ?? orgId,
      question: editing.question,
      answer: editing.answer,
      category: editing.category || "general",
      sport_id: editing.sport_id ?? null,
      is_active: editing.is_active ?? true,
      display_order: editing.display_order ?? 0,
    };
    let res;
    if (editing.id) {
      res = await (supabase as any).from("faq_catalog").update(payload).eq("id", editing.id);
    } else {
      res = await (supabase as any).from("faq_catalog").insert(payload);
    }
    if (res.error) {
      toast.error(res.error.message);
      return;
    }
    toast.success(editing.id ? "FAQ updated" : "FAQ created");
    setEditing(null);
    load();
  };

  const duplicate = async (it: FaqCatalogItem) => {
    const { error } = await (supabase as any).from("faq_catalog").insert({
      organization_id: it.organization_id ?? orgId,
      question: `${it.question} (Copy)`,
      answer: it.answer,
      category: it.category,
      sport_id: it.sport_id,
      is_active: true,
      display_order: items.length,
    });
    if (error) return toast.error(error.message);
    toast.success("Duplicated");
    load();
  };

  const toggleActive = async (it: FaqCatalogItem, value: boolean) => {
    const { error } = await (supabase as any)
      .from("faq_catalog")
      .update({ is_active: value })
      .eq("id", it.id);
    if (error) return toast.error(error.message);
    setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, is_active: value } : x)));
  };

  const archive = async (it: FaqCatalogItem) => {
    if (!confirm(`Archive "${it.question}"? It will remain linked to events but hidden from the catalog by default.`)) return;
    await toggleActive(it, false);
  };

  // Bulk
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const archiveSelected = async () => {
    if (!selected.size) return;
    if (!confirm(`Archive ${selected.size} FAQ(s)?`)) return;
    const { error } = await (supabase as any)
      .from("faq_catalog")
      .update({ is_active: false })
      .in("id", Array.from(selected));
    if (error) return toast.error(error.message);
    toast.success("Archived");
    setSelected(new Set());
    load();
  };

  const openLinkDialog = async () => {
    if (!selected.size) return;
    if (!orgId) return;
    const { data } = await (supabase as any)
      .from("events")
      .select("id, name")
      .eq("organization_id", orgId)
      .order("start_date", { ascending: false })
      .limit(200);
    setEventOptions(data ?? []);
    setChosenEvents(new Set());
    setLinkDialogOpen(true);
  };

  const linkSelected = async () => {
    if (!chosenEvents.size || !selected.size) return;
    const rows: any[] = [];
    Array.from(chosenEvents).forEach((eventId) => {
      Array.from(selected).forEach((faqId, idx) => {
        rows.push({
          event_id: eventId,
          faq_id: faqId,
          is_active: true,
          display_order: idx,
        });
      });
    });
    const { error } = await (supabase as any)
      .from("event_faq_links")
      .upsert(rows, { onConflict: "event_id,faq_id" });
    if (error) return toast.error(error.message);
    toast.success(`Linked ${selected.size} FAQ(s) to ${chosenEvents.size} event(s)`);
    setLinkDialogOpen(false);
    setSelected(new Set());
    load();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-primary" />
            FAQs
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Reusable FAQs that can be linked to multiple events. Manage answers
            in one place, edit per-event when needed.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" /> Add FAQ
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value={ALL}>All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          value={filterSport}
          onChange={(e) => setFilterSport(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value={ALL}>All sports</option>
          <option value={UNIVERSAL}>Universal</option>
          {sports.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="all">All</option>
          <option value="active">Active only</option>
          <option value="archived">Archived</option>
        </select>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search question or answer…"
            className="pl-8"
          />
        </div>

        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={openLinkDialog}>
              <Link2 className="w-4 h-4 mr-1" /> Link to events
            </Button>
            <Button size="sm" variant="outline" onClick={archiveSelected}>
              <Archive className="w-4 h-4 mr-1" /> Archive
            </Button>
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <HelpCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No FAQs found.</p>
          <Button className="mt-4" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> Create your first FAQ
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((it) => {
            const isOpen = !!expanded[it.id];
            return (
              <div
                key={it.id}
                className={`border border-border rounded-lg p-3 bg-card ${!it.is_active ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selected.has(it.id)}
                    onCheckedChange={() => toggleSelect(it.id)}
                    className="mt-1"
                  />
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [it.id]: !isOpen }))}
                    className="mt-0.5 text-muted-foreground hover:text-foreground"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">{it.question}</span>
                      <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-secondary text-foreground border-border">
                        {it.category || "general"}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border bg-blue-100 text-blue-700 border-blue-200">
                        {sportName(it.sport_id)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Linked to {linkCounts[it.id] ?? 0} event{(linkCounts[it.id] ?? 0) === 1 ? "" : "s"}
                      </span>
                    </div>
                    {isOpen && (
                      <div className="mt-3 pl-1">
                        <RichTextDisplay html={it.answer} />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="flex items-center gap-1 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={it.is_active}
                        onChange={(e) => toggleActive(it, e.target.checked)}
                      />
                      Active
                    </label>
                    <button
                      onClick={() => setEditing(it)}
                      className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => duplicate(it)}
                      className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => archive(it)}
                      className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit FAQ" : "New FAQ"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Question *</label>
                <Input
                  value={editing.question ?? ""}
                  onChange={(e) => setEditing({ ...editing, question: e.target.value })}
                  placeholder="What time do games start?"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Answer *</label>
                <RichTextEditor
                  value={editing.answer ?? ""}
                  onChange={(html) => setEditing({ ...editing, answer: html })}
                  placeholder="Type the answer…"
                  minHeight="160px"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <Input
                    list="faq-cats"
                    value={editing.category ?? ""}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    placeholder="general"
                  />
                  <datalist id="faq-cats">
                    {categories.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sport</label>
                  <select
                    value={editing.sport_id ?? UNIVERSAL}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        sport_id: e.target.value === UNIVERSAL ? null : e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value={UNIVERSAL}>Universal (all sports)</option>
                    {sports.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_active ?? true}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to events dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link {selected.size} FAQ(s) to events</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-1 border border-border rounded-lg p-2">
            {eventOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No events found.</p>
            ) : (
              eventOptions.map((ev) => (
                <label key={ev.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary cursor-pointer">
                  <Checkbox
                    checked={chosenEvents.has(ev.id)}
                    onCheckedChange={() => {
                      const n = new Set(chosenEvents);
                      n.has(ev.id) ? n.delete(ev.id) : n.add(ev.id);
                      setChosenEvents(n);
                    }}
                  />
                  <span className="text-sm">{ev.name}</span>
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button onClick={linkSelected} disabled={!chosenEvents.size}>
              Link to {chosenEvents.size} event{chosenEvents.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
